import { useContext, useState, useEffect, useMemo, useCallback } from "react";
import { ModalContext } from "../../../utils/modal/ModalProvider";
import { IAM_SERVER_SERVICE_USER, IAM_SERVER_SERVICE_GROUP } from "../../../utils/Config";
import axios from "../../../utils/api/axios";
import styles from './GroupUserRegisterModal.module.css';
import { useInfiniteScroll } from "./useInfiniteScroll";
import { InnocodeButton, InnocodeDatatable, useDialog } from '@intsoft/innocode-core';

const fetchAssignedUsers = async ({ groupId, selectedServiceId, page, size, searchKeyword }) => {
    const response = await axios.post(`${IAM_SERVER_SERVICE_GROUP}/get-add/${groupId}/users`, {
        serviceId: selectedServiceId, page, size, searchKeyword
    });
    return response.data.data; // { data: [...], last: false } 구조를 반환해야 함
};

const fetchAvailableUsers = async ({ groupId, selectedServiceId, page, size, searchKeyword }) => {
    const response = await axios.post(`${IAM_SERVER_SERVICE_GROUP}/get-add/${groupId}/available-users`, {
        serviceId: selectedServiceId, page, size, searchKeyword
    });
    return response.data.data;
};

export const GroupUserRegisterModal = ({ update, selectedServiceId, groupId, onClose }) => { 
    const { showDialog } = useDialog();
    const { 
        items: assignedUsers,
        setItems: setAssignedUsers, 
        loading: loadingAssigned, 
        hasMore: hasMoreAssigned, 
        lastItemRef: assignedLastItemRef, 
        search: searchAssigned } = useInfiniteScroll(fetchAssignedUsers, { groupId, selectedServiceId });
    const { items: availableUsers, 
        setItems: setAvailableUsers, 
        loading: loadingAvailable, 
        hasMore: hasMoreAvailable, 
        lastItemRef: availableLastItemRef, 
        search: searchAvailable } = useInfiniteScroll(fetchAvailableUsers, { groupId, selectedServiceId });

    const [leftCheckedUsers, setLeftCheckedUsers] = useState([]);
    const [rightCheckedUsers, setRightCheckedUsers] = useState([]);
    const [isLeftSelectAll, setIsLeftSelectAll] = useState(false);
    const [isRightSelectAll, setIsRightSelectAll] = useState(false);
    const [initialAssignedIds, setInitialAssignedIds] = useState(null);
    const [tableResetKey, setTableResetKey] = useState(0);


    // 컴포넌트가 처음 마운트될 때 각 목록의 첫 페이지를 로드
    useEffect(() => {
        searchAssigned();
        searchAvailable();
    }, []);

    // assignedUsers가 처음 로드되었을 때, 그 상태를 "수정 전 원본"으로 저장합니다.
    useEffect(() => {
        // initialAssignedIds가 아직 설정되지 않았고(null),
        // assignedUsers의 로딩이 막 끝났을 때(!loadingAssigned) 이 로직을 실행합니다.
        if (initialAssignedIds === null && !loadingAssigned) {
            
            // 이 시점의 assignedUsers가 바로 "수정 전 원본"입니다.
            // 사용자가 0명인 경우라도, 빈 Set으로 초기화하는 것이 중요합니다.
            setInitialAssignedIds(new Set(assignedUsers.map(u => u.profileId)));
        }
        
    }, [loadingAssigned]); 

    // 전체선택 핸들러 (좌측)
    useEffect(() => {
        if (isLeftSelectAll) {
            setLeftCheckedUsers(availableUsers);
        } else {
            setLeftCheckedUsers([]);
        }
    }, [isLeftSelectAll, availableUsers]);

    // 전체선택 핸들러 (우측)
    useEffect(() => {
        if (isRightSelectAll) {
            setRightCheckedUsers(assignedUsers);
        } else {
            setRightCheckedUsers([]);
        }
    }, [isRightSelectAll, assignedUsers]);

    // 왼쪽 → 오른쪽 (그룹에 추가)
    const handleAssign = useCallback(() => {
        if (leftCheckedUsers.length === 0) return;
        
        setAssignedUsers(prev => [...prev, ...leftCheckedUsers].sort((a, b) => a.name.localeCompare(b.name)));
        setAvailableUsers(prev => prev.filter(user => !leftCheckedUsers.some(u => u.profileId === user.profileId)));
        setLeftCheckedUsers([]);
        setIsLeftSelectAll(false);
        setTableResetKey(prev => prev + 1);
    }, [leftCheckedUsers, setAssignedUsers, setAvailableUsers]);

    // 오른쪽 → 왼쪽 (그룹에서 제거)
    const handleUnassign = useCallback(() => {
        if (rightCheckedUsers.length === 0) return;
        
        setAvailableUsers(prev => [...prev, ...rightCheckedUsers].sort((a, b) => a.name.localeCompare(b.name)));
        setAssignedUsers(prev => prev.filter(user => !rightCheckedUsers.some(u => u.profileId === user.profileId)));
        setRightCheckedUsers([]);
        setIsRightSelectAll(false);
        setTableResetKey(prev => prev + 1);
    }, [rightCheckedUsers, setAssignedUsers, setAvailableUsers]);

    // 저장 로직
    const handleSave = useCallback(() => {
        if (initialAssignedIds === null) {
            onClose();
            return;
        }

        const finalAssignedIds = new Set(assignedUsers.map(u => u.profileId));
        const added = [...finalAssignedIds].filter(id => !initialAssignedIds.has(id));
        const removed = [...initialAssignedIds].filter(id => !finalAssignedIds.has(id));

        Promise.all([
            ...(added.length > 0 ? [
                axios.post(`${IAM_SERVER_SERVICE_GROUP}/update/${groupId}/users`, {
                    serviceId: selectedServiceId,
                    userIds: added
                })
            ] : []),
            ...(removed.length > 0 ? [
                axios.post(`${IAM_SERVER_SERVICE_GROUP}/delete/${groupId}/users`, {
                    serviceId: selectedServiceId,
                    userIds: removed
                })
            ] : []),
        ])
            .then(() => {
                showDialog({
                    iconType: "check",
                    type: 'info',
                    title: "성공",
                    message: "저장되었습니다.",
                    confirmText: "확인",
                    onConfirm: () => {
                        update(groupId);
                        onClose();
                    }
                });
            })
            .catch(err => {
                showDialog({
                    iconType: "warning",
                    type: 'error',
                    title: "실패",
                    message: err.response?.data?.message || "저장 실패",
                    confirmText: "확인",
                });
            });
    }, [initialAssignedIds, assignedUsers, groupId, selectedServiceId, update, onClose ]);

    // 좌측 테이블 체크박스 변경
    const handleLeftRowSelectionChange = useCallback((row, index, isChecked) => {
        if (isChecked) {
            setLeftCheckedUsers(prev => [...prev, row]);
        } else {
            setLeftCheckedUsers(prev => prev.filter(u => u.profileId !== row.profileId));
        }
    }, []);

    // 우측 테이블 체크박스 변경
    const handleRightRowSelectionChange = useCallback((row, index, isChecked) => {
        if (isChecked) {
            setRightCheckedUsers(prev => [...prev, row]);
        } else {
            setRightCheckedUsers(prev => prev.filter(u => u.profileId !== row.profileId));
        }
    }, []);

    // 미선택 테이블 컬럼 정의
    const leftColumns = useMemo(() => [
        {
            id: 'user',
            header: '서비스 유저',
            accessorKey: 'userId',
            enableSorting: false,
            cell: ({ row }) => (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <img
                        src={row.imgUrl || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                        alt="프로필 이미지"
                        style={{ width: "28px", height: "28px", borderRadius: "50%" }}
                    />
                    <span className={styles.cellText}>{`${row.name} (${row.userId})`}</span>
                </div>
            ),
        },
    ], []);

    // 선택됨 테이블 컬럼 정의
    const rightColumns = useMemo(() => [
        {
            id: 'user',
            header: '그룹 유저',
            accessorKey: 'userId',
            enableSorting: false,
            cell: ({ row }) => (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <img
                        src={row.imgUrl || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                        alt="프로필 이미지"
                        style={{ width: "28px", height: "28px", borderRadius: "50%" }}
                    />
                    <span className={styles.cellText}>{`${row.name} (${row.userId})`}</span>
                </div>
            ),
        },
    ], []);

    return (
        <div className={styles.layoutContainer}>
            <div className={styles.scrollContentArea}>
                <div className={styles.minHeightArea}>
                    <div className={styles.contentAreaWrapper}>
                        <div className={styles.contentAreaContent}>
                            <div className={styles.contentAreaTitle}>
                                <h1 className={`innocode-typography--headline-md innocode-typography--bold ${styles.contentAreaTitleText}`}>
                                    유저 등록
                                </h1>
                                <InnocodeButton
                                    theme="opacity"
                                    designType='assistive'
                                    enablePadding={false}
                                    iconOnly={true}
                                    icon={
                                        <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18 17.94 6M18 18 6.06 6" />
                                        </svg>
                                    }
                                    onClick={onClose}
                                />
                            </div>
                            <div className={styles.contentAreaList}>
                                <div className={styles.userSelectionContainer}>
                                    {/* 좌측 - 미선택 유저 리스트 */}
                                    <div className={styles.tableSection}>
                                        <div className={styles.tableSectionHeader}>
                                            <span className={styles.tableSectionTitle}>미선택</span>
                                        </div>
                                        <div className={styles.tableWrapper}>
                                            <InnocodeDatatable
                                                key={`left-${tableResetKey}-${isLeftSelectAll}`}
                                                className={styles.datatable}
                                                data={availableUsers}
                                                columns={leftColumns}
                                                enableCustomHeader={true}
                                                headerCheckboxSelectsAll={isLeftSelectAll}
                                                manualPagination={false}
                                                enableSorting={false}
                                                enableCheckbox={true}
                                                isLoading={loadingAvailable}
                                                emptyMessage="유저가 없습니다."
                                                onRowSelectionChange={handleLeftRowSelectionChange}
                                                onSelectAllChange={(isSelected) => setIsLeftSelectAll(isSelected)}
                                                enableInfiniteScroll={true}
                                            />
                                        </div>
                                    </div>

                                    {/* 중앙 - 이동 버튼 */}
                                    <div className={styles.centerButtons}>
                                        <InnocodeButton
                                            size="sm"
                                            designType="primary"
                                            theme="line"
                                            onClick={handleAssign}
                                            disabled={leftCheckedUsers.length === 0}
                                            iconOnly={true}
                                            icon={
                                                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 12H5m14 0-4 4m4-4-4-4"/>
                                                </svg>
                                            }
                                        />
                                        <InnocodeButton
                                            size="sm"
                                            designType="assistive"
                                            theme="line"
                                            onClick={handleUnassign}
                                            disabled={rightCheckedUsers.length === 0}
                                            iconOnly={true}
                                            icon={
                                                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12l4-4m-4 4 4 4"/>
                                                </svg>
                                            }
                                        />
                                    </div>

                                    {/* 우측 - 선택된 유저 리스트 */}
                                    <div className={styles.tableSection}>
                                        <div className={styles.tableSectionHeader}>
                                            <span className={styles.tableSectionTitle}>선택됨</span>
                                        </div>
                                        <div className={styles.tableWrapper}>
                                            <InnocodeDatatable
                                                className={styles.datatable}
                                                key={`right-${tableResetKey}-${isRightSelectAll}`}
                                                data={assignedUsers}
                                                columns={rightColumns}
                                                enableCustomHeader={true}
                                                headerCheckboxSelectsAll={isRightSelectAll}
                                                manualPagination={false}
                                                enableSorting={false}
                                                enableCheckbox={true}
                                                enableInfiniteScroll={true}
                                                isLoading={loadingAssigned}
                                                emptyMessage="유저가 없습니다."
                                                onRowSelectionChange={handleRightRowSelectionChange}
                                                onSelectAllChange={(isSelected) => setIsRightSelectAll(isSelected)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.actionButtonWrapper}>
                                    <InnocodeButton
                                        className={styles.actionButton}
                                        theme="opacity"
                                        designType="primary"
                                        size="lg"
                                        onClick={onClose}
                                    >
                                        취소
                                    </InnocodeButton>
                                    <InnocodeButton
                                        className={styles.actionButton}
                                        theme="solid"
                                        size="lg"
                                        onClick={handleSave}
                                    >
                                        저장
                                    </InnocodeButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
